import fs from 'node:fs';
import os from 'node:os';
import path from 'node:path';
import { spawnSync } from 'node:child_process';
import { writePrivateFileSync } from './private-file.js';

const KEYCHAIN_SERVICE = 'standardagents-cli';

export interface CliOrgAuthRecord {
  org_id: string;
  org_slug: string;
  org_name: string;
  user_id: string;
  user_email: string;
  token_storage: 'keychain' | 'file';
  keychain_account?: string;
  refresh_token?: string;
  updated_at: number;
}

export interface CliAuthState {
  active_org_id: string | null;
  orgs: Record<string, CliOrgAuthRecord>;
  git_auth?: CliGitAuthRecord | null;
}

export interface CliGitAuthRecord {
  version: 1;
  endpoint: string;
  session_cookie: string;
  /** Long-lived account API key minted at git-login. Preferred over the
   *  session cookie when minting git tokens because sessions expire in days
   *  while keys live until revoked — this makes git-login a once-per-machine
   *  event instead of a recurring chore. */
  api_key?: string;
  account_id: string;
  account_slug?: string;
  user_id: string;
  user_email: string;
  updated_at: number;
}

interface SaveCliAuthInput {
  org: { id: string; slug: string; name: string };
  user: { id: string; email: string };
  refreshToken: string;
}

interface ResolveTargetResult {
  orgId: string;
  record: CliOrgAuthRecord;
}

function configDir(): string {
  if (process.platform === 'win32') {
    const appData = process.env.APPDATA;
    if (appData) return path.join(appData, 'standardagents');
  }
  const xdgConfig = process.env.XDG_CONFIG_HOME;
  if (xdgConfig) return path.join(xdgConfig, 'standardagents');
  return path.join(os.homedir(), '.config', 'standardagents');
}

function stateFilePath(): string {
  return path.join(configDir(), 'auth-state.json');
}

function ensureStateDir(): void {
  fs.mkdirSync(path.dirname(stateFilePath()), { recursive: true });
}

function securityCommand(
  args: string[],
): { ok: boolean; stdout: string; stderr: string } {
  const result = spawnSync('security', args, { encoding: 'utf8' });
  if (result.error) {
    return { ok: false, stdout: '', stderr: result.error.message };
  }
  return {
    ok: result.status === 0,
    stdout: (result.stdout || '').trim(),
    stderr: (result.stderr || '').trim(),
  };
}

function keychainAccountForOrg(orgId: string): string {
  return `org:${orgId}`;
}

function storeInKeychain(orgId: string, refreshToken: string): { ok: boolean; account: string } {
  if (process.platform !== 'darwin') {
    return { ok: false, account: keychainAccountForOrg(orgId) };
  }

  const account = keychainAccountForOrg(orgId);
  const command = securityCommand([
    'add-generic-password',
    '-a',
    account,
    '-s',
    KEYCHAIN_SERVICE,
    '-w',
    refreshToken,
    '-U',
  ]);
  return { ok: command.ok, account };
}

function deleteFromKeychain(orgId: string): void {
  if (process.platform !== 'darwin') return;
  const account = keychainAccountForOrg(orgId);
  securityCommand([
    'delete-generic-password',
    '-a',
    account,
    '-s',
    KEYCHAIN_SERVICE,
  ]);
}

function readFromKeychain(orgId: string): string | null {
  if (process.platform !== 'darwin') return null;
  const account = keychainAccountForOrg(orgId);
  const command = securityCommand([
    'find-generic-password',
    '-a',
    account,
    '-s',
    KEYCHAIN_SERVICE,
    '-w',
  ]);
  if (!command.ok || !command.stdout) return null;
  return command.stdout;
}

export function readCliAuthState(): CliAuthState {
  const file = stateFilePath();
  if (!fs.existsSync(file)) {
    return { active_org_id: null, orgs: {} };
  }

  try {
    const parsed = JSON.parse(fs.readFileSync(file, 'utf8')) as Partial<CliAuthState>;
    return {
      active_org_id: parsed.active_org_id ?? null,
      orgs: parsed.orgs ?? {},
      git_auth: parseCliGitAuthRecord(parsed.git_auth),
    };
  } catch {
    return { active_org_id: null, orgs: {} };
  }
}

function parseCliGitAuthRecord(value: unknown): CliGitAuthRecord | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<CliGitAuthRecord>;
  if (candidate.version !== 1) return null;
  if (
    typeof candidate.endpoint !== 'string' ||
    typeof candidate.session_cookie !== 'string' ||
    typeof candidate.account_id !== 'string' ||
    typeof candidate.user_id !== 'string' ||
    typeof candidate.user_email !== 'string' ||
    typeof candidate.updated_at !== 'number'
  ) {
    return null;
  }
  return {
    version: 1,
    endpoint: candidate.endpoint,
    session_cookie: candidate.session_cookie,
    ...(typeof candidate.api_key === 'string' && candidate.api_key.length > 0
      ? { api_key: candidate.api_key }
      : {}),
    account_id: candidate.account_id,
    account_slug: typeof candidate.account_slug === 'string' ? candidate.account_slug : undefined,
    user_id: candidate.user_id,
    user_email: candidate.user_email,
    updated_at: candidate.updated_at,
  };
}

function writeCliAuthState(state: CliAuthState): void {
  ensureStateDir();
  writePrivateFileSync(stateFilePath(), JSON.stringify(state, null, 2) + '\n');
}

export function hasStoredCliAuth(): boolean {
  const state = readCliAuthState();
  return Object.keys(state.orgs).length > 0;
}

export function saveCliGitAuth(input: {
  endpoint: string;
  sessionCookie: string;
  apiKey?: string | null;
  account: { id: string; slug?: string };
  user: { id: string; email: string };
}): CliGitAuthRecord {
  const state = readCliAuthState();
  const record: CliGitAuthRecord = {
    version: 1,
    endpoint: input.endpoint.replace(/\/+$/, ''),
    session_cookie: input.sessionCookie,
    ...(input.apiKey ? { api_key: input.apiKey } : {}),
    account_id: input.account.id,
    account_slug: input.account.slug,
    user_id: input.user.id,
    user_email: input.user.email,
    updated_at: Date.now(),
  };
  state.git_auth = record;
  writeCliAuthState(state);
  return record;
}

export function resolveCliGitAuth(): CliGitAuthRecord | null {
  return readCliAuthState().git_auth ?? null;
}

export function clearCliGitAuth(): CliGitAuthRecord | null {
  const state = readCliAuthState();
  const existing = state.git_auth ?? null;
  state.git_auth = null;
  writeCliAuthState(state);
  return existing;
}

export function saveCliAuth(input: SaveCliAuthInput): { storage: 'keychain' | 'file' } {
  const state = readCliAuthState();
  const now = Date.now();
  const keychain = storeInKeychain(input.org.id, input.refreshToken);
  const allowPlaintextFallback = process.env.STANDARDAGENTS_ALLOW_PLAINTEXT_TOKEN === '1';

  let token_storage: 'keychain' | 'file';
  let keychain_account: string | undefined;
  let refresh_token: string | undefined;

  if (keychain.ok) {
    token_storage = 'keychain';
    keychain_account = keychain.account;
  } else if (allowPlaintextFallback) {
    token_storage = 'file';
    refresh_token = input.refreshToken;
  } else {
    throw new Error(
      'Unable to store refresh token in keychain. Set STANDARDAGENTS_ALLOW_PLAINTEXT_TOKEN=1 for explicit file fallback or use STANDARDAGENTS_TOKEN in CI.',
    );
  }

  state.orgs[input.org.id] = {
    org_id: input.org.id,
    org_slug: input.org.slug,
    org_name: input.org.name,
    user_id: input.user.id,
    user_email: input.user.email,
    token_storage,
    keychain_account,
    refresh_token,
    updated_at: now,
  };
  state.active_org_id = input.org.id;
  writeCliAuthState(state);

  return { storage: token_storage };
}

function resolveTargetOrgId(state: CliAuthState, orgInput?: string): string | null {
  if (orgInput) {
    const trimmed = orgInput.trim();
    if (!trimmed) return null;

    if (state.orgs[trimmed]) return trimmed;

    const match = Object.values(state.orgs).find(
      (record) => record.org_slug === trimmed,
    );
    return match?.org_id ?? null;
  }

  if (state.active_org_id && state.orgs[state.active_org_id]) {
    return state.active_org_id;
  }

  const first = Object.keys(state.orgs)[0];
  return first ?? null;
}

function resolveTargetRecord(state: CliAuthState, orgInput?: string): ResolveTargetResult | null {
  const orgId = resolveTargetOrgId(state, orgInput);
  if (!orgId) return null;
  const record = state.orgs[orgId];
  if (!record) return null;
  return { orgId, record };
}

export function resolveStoredCliAuth(orgInput?: string): CliOrgAuthRecord | null {
  const state = readCliAuthState();
  const resolved = resolveTargetRecord(state, orgInput);
  return resolved?.record ?? null;
}

export function readStoredRefreshToken(orgInput?: string): {
  record: CliOrgAuthRecord;
  refreshToken: string;
} | null {
  const explicit = process.env.STANDARDAGENTS_TOKEN?.trim();
  if (explicit) {
    return {
      record: {
        org_id: 'env',
        org_slug: 'env',
        org_name: 'Environment token',
        user_id: 'env',
        user_email: 'env',
        token_storage: 'file',
        updated_at: Date.now(),
      },
      refreshToken: explicit,
    };
  }

  const state = readCliAuthState();
  const resolved = resolveTargetRecord(state, orgInput);
  if (!resolved) return null;
  const { record } = resolved;

  if (record.token_storage === 'file') {
    if (!record.refresh_token) return null;
    return { record, refreshToken: record.refresh_token };
  }

  const keychainToken = readFromKeychain(record.org_id);
  if (!keychainToken) return null;
  return { record, refreshToken: keychainToken };
}

export function updateStoredRefreshToken(
  orgId: string,
  refreshToken: string,
): { storage: 'keychain' | 'file' } {
  const state = readCliAuthState();
  const existing = state.orgs[orgId];
  if (!existing) {
    throw new Error(`No stored auth session found for org "${orgId}".`);
  }

  const allowPlaintextFallback = process.env.STANDARDAGENTS_ALLOW_PLAINTEXT_TOKEN === '1';
  let storage: 'keychain' | 'file';
  if (existing.token_storage === 'keychain') {
    const keychainWrite = storeInKeychain(orgId, refreshToken);
    if (keychainWrite.ok) {
      storage = 'keychain';
      existing.keychain_account = keychainWrite.account;
      existing.refresh_token = undefined;
    } else if (allowPlaintextFallback) {
      storage = 'file';
      existing.token_storage = 'file';
      existing.keychain_account = undefined;
      existing.refresh_token = refreshToken;
    } else {
      throw new Error(
        'Unable to rotate refresh token in keychain. Set STANDARDAGENTS_ALLOW_PLAINTEXT_TOKEN=1 for explicit file fallback.',
      );
    }
  } else {
    storage = 'file';
    existing.refresh_token = refreshToken;
  }

  existing.updated_at = Date.now();
  state.orgs[orgId] = existing;
  writeCliAuthState(state);

  return { storage };
}

export function clearCliAuth(orgInput?: string): CliOrgAuthRecord | null {
  const state = readCliAuthState();
  const resolved = resolveTargetRecord(state, orgInput);
  if (!resolved) return null;
  const { orgId: targetOrgId, record } = resolved;

  if (record.token_storage === 'keychain') {
    deleteFromKeychain(targetOrgId);
  }

  delete state.orgs[targetOrgId];

  if (state.active_org_id === targetOrgId) {
    const next = Object.keys(state.orgs)[0];
    state.active_org_id = next ?? null;
  }

  writeCliAuthState(state);
  return record;
}
